/**
 * @fileoverview Hook personalizado para sincronização dos favoritos.
 * Gerencia sincronização automática entre favoritos local e remoto.
 * 
 * @module useFavoritesSync
 * @since 1.0.0
 */

import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadFavoritesAsync, saveFavoritesAsync } from '../store/slices/favoritesSlice';

/**
 * Hook para sincronização automática dos favoritos.
 * Carrega favoritos na inicialização e salva automaticamente nas mudanças.
 * 
 * @returns {Object} Funções e estado de sincronização
 */
export const useFavoritesSync = () => {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { items: favoritesItems, isLoading, error, needsSync } = useSelector((state) => state.favorites);

  /**
   * Carrega favoritos da sessão ativa se usuário autenticado
   */
  const loadRemoteFavorites = useCallback(() => {
    if (isAuthenticated && user?.email) {
      dispatch(loadFavoritesAsync());
    }
  }, [dispatch, isAuthenticated, user?.email]);

  /**
   * Salva favoritos na sessão ativa se usuário autenticado
   */
  const saveRemoteFavorites = useCallback(() => {
    if (isAuthenticated && user?.email && needsSync) {
      // map: converte itens dos favoritos para formato da API com tipos corretos
      const favoritesData = favoritesItems.map(item => ({
        productId: String(item.productId),
        name: String(item.name || ''),
        brand: String(item.brand || ''),
        image: String(item.image || ''),
        category: String(item.category || '')
      }));
      
      dispatch(saveFavoritesAsync(favoritesData));
    }
  }, [dispatch, isAuthenticated, user?.email, favoritesItems, needsSync]);

  /**
   * Carrega favoritos na inicialização se usuário autenticado
   */
  useEffect(() => {
    loadRemoteFavorites();
  }, [loadRemoteFavorites]);

  /**
   * Sincroniza automaticamente quando há mudanças nos favoritos
   * Evita sincronização durante logout
   */
  useEffect(() => {
    if (needsSync && isAuthenticated) {
      const timeoutId = setTimeout(() => {
        // Verificar novamente se ainda está autenticado antes de sincronizar
        if (isAuthenticated && user?.email) {
          saveRemoteFavorites();
        }
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [needsSync, saveRemoteFavorites, isAuthenticated, user?.email]);

  return {
    loadRemoteFavorites,
    saveRemoteFavorites,
    isLoading,
    error
  };
};